import React from 'react';
import {
  Select,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Form
} from 'antd';
import dayjs from 'dayjs';
import { DeleteOutlined } from '@ant-design/icons';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  CustomersFilter,
  CustomersFilterOperator,
  CustomersFilterOperatorType,
  customersFilterPropertiesOptions,
  CustomersFilterProperty
} from '@next-is-free-monorepo/next-is-free-types';
import { apiV1 } from '../../utils/apiClient';


export const operatorOptions: Record<CustomersFilterOperatorType, {
  label: string;
  value: CustomersFilterOperator
}[]> = {
  number: [
    { label: '= Uguale a', value: 'equal' },
    { label: '≠ Diverso da', value: 'not_equal' },
    { label: '> Maggiore di', value: 'more_than' },
    { label: '< Minore di', value: 'less_than' }
  ],
  date: [
    { label: '= Uguale a', value: 'equal' },
    { label: '≠ Diverso da', value: 'not_equal' },
    { label: 'Dopo di', value: 'after' },
    { label: 'Prima di', value: 'before' }
  ],
  enum: [
    { label: '= Uguale a', value: 'equal' },
    { label: '≠ Diverso da', value: 'not_equal' }
  ],
  string: [
    { label: '= Uguale a', value: 'equal' },
    { label: '≠ Diverso da', value: 'not_equal' }
  ]
};

type RawCustomersFilter = {
  property: CustomersFilterProperty;
  operator: CustomersFilterOperator;
  value: string | number | Date;
};

interface Props {
  value?: CustomersFilter[];
  onChange?: (filters: RawCustomersFilter[]) => void;
}


const fetchEnumValues = async (property: CustomersFilterProperty): Promise<string[]> => {
  const res = await fetch(`/api/enums/${property}`);
  if (!res.ok) throw new Error('Failed to load enum values');
  return res.json();
};

const FilterRow = ({
                     filter,
                     onChange,
                     onDelete,
                     value
                   }: {
  filter: RawCustomersFilter;
  onChange: (filter: RawCustomersFilter) => void;
  onDelete: () => void;
  value: RawCustomersFilter[]
}) => {
  const selectedProperty = customersFilterPropertiesOptions.find((p) => p.value === filter.property);
  const propertyType = selectedProperty?.type || 'string';

  const { data: enumValues } = useQuery({
    queryKey: ['enum-values', filter.property],
    queryFn: () => fetchEnumValues(filter.property),
    enabled: false
  });

  const metadata = useSuspenseQuery({
    queryKey: ['customers-metadata'], queryFn: async () => {
      return await apiV1.get<{ key: string }[]>('/shop/customers/metadata');
    },
    staleTime: Infinity
  });

  const propertiesOptions = [{
    label: 'Proprietà',
    title: 'Proprietà',
    options: customersFilterPropertiesOptions.map((p) => ({ label: p.label, value: p.value }))
  },
    {
      label: 'Campi Personalizzati',
      title: 'metadata',
      options: metadata.data.map((item) => ({ label: item.key, value: 'metadata_' + item.key })) //add metadata_ to avoid confusion with standard properties
    }
  ];

  return (
    <div className="flex gap-2 mb-4 items-center">
      <div className={'flex w-full flex-wrap gap-2'}>
        <Select
          className="min-w-[300px] flex-1"
          value={filter.property}
          onChange={(val) =>
            onChange({
              property: val,
              operator: 'equal',
              value: ''
            })
          }
          placeholder={'Proprietà'}
          options={propertiesOptions}
        />
        <Select
          className="flex-1 min-w-[150px]"
          value={filter.operator}
          onChange={(val) => onChange({ ...filter, operator: val })}
          options={operatorOptions[propertyType]}
        />

        {propertyType === 'string' && (
          <Input
            className="flex-1 min-w-[150px]"
            value={filter.value as string}
            onChange={(e) => onChange({ ...filter, value: e.target.value })}
            placeholder={'Valore'}
          />
        )}

        {propertyType === 'number' && (
          <InputNumber
            className="flex-1 min-w-[150px]"
            value={filter.value as number}
            onChange={(val) => onChange({ ...filter, value: val ?? 0 })}
            placeholder={'Numero'}
          />
        )}

        {propertyType === 'date' && (
          <DatePicker
            className="flex-1 min-w-[150px]"
            value={dayjs(filter.value as string)}
            onChange={(val) =>
              onChange({ ...filter, value: val ? val.toISOString() : '' })
            }
          />
        )}

        {propertyType === 'enum' && (
          <Select
            className="flex-1 min-w-[150px]"
            value={filter.value as string}
            onChange={(val) => onChange({ ...filter, value: val })}
            options={(enumValues || []).map((v) => ({ label: v, value: v }))}
          />
        )}
      </div>
      {value.length > 1 &&
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={onDelete}
        />
      }
    </div>
  );
};

const CustomerFiltersInput: React.FC<Props> = ({ value = [], onChange }) => {


  const handleChange = (index: number, newFilter: RawCustomersFilter) => {
    const newFilters = [...value];
    newFilters[index] = newFilter;
    onChange?.(newFilters);
  };

  const handleAdd = () => {
    onChange?.([
      ...value,
      { property: 'age', operator: 'equal', value: 0 }
    ]);
  };

  const handleDelete = (index: number) => {
    const newFilters = [...value];
    newFilters.splice(index, 1);
    onChange?.(newFilters);
  };

  return (
    <div>
      {value.map((filter, index) => (
        <FilterRow
          key={index}
          value={value}
          filter={filter}
          onChange={(updated) => handleChange(index, updated)}
          onDelete={() => handleDelete(index)}
        />
      ))}
    </div>
  );
};

export default CustomerFiltersInput;
